import { useState, useEffect, useRef, useCallback } from 'react';

import { getRunningBuildId } from '~helpers/buildId';

// How often to poll while the tab stays open. Focus/visibility changes catch
// returning users right away, so this interval is only a backstop for tabs left
// open for a long time.
const VERSION_CHECK_INTERVAL_MS = 15 * 60 * 1000;

// Skip a check if one already ran this recently, so focus + visibilitychange +
// interval firing together don't cause a burst of requests.
const MIN_CHECK_GAP_MS = 30 * 1000;

const idleState = { updateAvailable: false, latestBuildId: null, forced: false };

// Detects when a newer frontend build has been deployed by polling the origin
// `/version` endpoint and comparing it against the build the client is running.
// It only reports availability; showing UI and reloading is handled elsewhere.
const useVersionCheck = () => {
  if (typeof window === 'undefined') {
    return idleState;
  }

  const [state, setState] = useState(idleState);
  const lastCheckRef = useRef(0);
  const inFlightRef = useRef(false);

  const check = useCallback(async () => {
    // Once an update is known, there is nothing left to detect.
    if (state.updateAvailable || inFlightRef.current) {
      return;
    }
    if (Date.now() - lastCheckRef.current < MIN_CHECK_GAP_MS) {
      return;
    }

    inFlightRef.current = true;
    lastCheckRef.current = Date.now();

    try {
      // Cloudflare caches bare /version despite the origin's no-store header.
      // A query parameter bypasses the public HTML cache rule.
      const response = await fetch(`/version?check=${Date.now()}`, { cache: 'no-store' });
      if (!response.ok) {
        return;
      }
      const data = await response.json();
      if (data && data.buildId && data.buildId !== getRunningBuildId()) {
        setState({
          updateAvailable: true,
          latestBuildId: data.buildId,
          forced: !!data.forced,
        });
      }
    } catch (error) {
      // Network hiccup or offline - never surface a false update prompt.
    } finally {
      inFlightRef.current = false;
    }
  }, [state.updateAvailable]);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        check();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', check);
    const interval = setInterval(check, VERSION_CHECK_INTERVAL_MS);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', check);
      clearInterval(interval);
    };
  }, [check]);

  return state;
};

export default useVersionCheck;
